import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { FileText, Mail, Calendar } from 'lucide-react';
import SEO from '../../components/SEO';

const TermsPage = () => {
  const lastUpdated = '17 Aralık 2025';

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <SEO 
        title="Kullanım Koşulları"
        description="Borsamex platform kullanım koşulları, üyelik sözleşmesi ve hizmet şartları."
        url="/legal/terms"
      />
      
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 px-3 py-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
            <FileText className="h-3 w-3 mr-1" /> Yasal
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-4">
            Kullanım Koşulları
          </h1>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Calendar className="h-4 w-4" />
            <span>Son güncelleme: {lastUpdated}</span>
          </div>
        </div>

        {/* Content */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 prose prose-invert max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Genel Hükümler</h2>
              <p className="text-slate-300 mb-4">
                Bu Kullanım Koşulları ("Sözleşme"), <strong className="text-white">Datex Bilişim AŞ</strong> ("Şirket") 
                tarafından işletilen Borsamex platformu ("Platform") ile kullanıcılar ("Kullanıcı") arasındaki 
                hukuki ilişkiyi düzenler.
              </p>
              <p className="text-slate-300">
                Platforma kayıt olarak veya platformu kullanarak bu koşulları kabul etmiş sayılırsınız.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Tanımlar</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li><strong className="text-white">Platform:</strong> Borsamex web sitesi ve ilgili tüm hizmetler</li>
                <li><strong className="text-white">Kullanıcı:</strong> Platforma kayıtlı gerçek veya tüzel kişiler</li>
                <li><strong className="text-white">Strateji:</strong> Kullanıcılar tarafından oluşturulan veya takip edilen trading stratejileri</li>
                <li><strong className="text-white">Sinyal:</strong> Stratejiler tarafından üretilen al-sat bildirimleri</li>
                <li><strong className="text-white">Strateji Geliştirici:</strong> Strateji oluşturan ve yayınlayan kullanıcılar</li>
                <li><strong className="text-white">Strateji Takipçisi:</strong> Stratejilere abone olan kullanıcılar</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Üyelik ve Hesap</h2>
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Üyelik Şartları</h3>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>18 yaşını doldurmuş olmak</li>
                <li>Geçerli ve doğru bilgiler vermek</li>
                <li>Türkiye Cumhuriyeti veya hizmet verilen ülkelerde ikamet etmek</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-white mb-3">3.2 Hesap Güvenliği</h3>
              <p className="text-slate-300">
                Hesap bilgilerinizin güvenliğinden siz sorumlusunuz. Şifrenizi kimseyle paylaşmamalı, 
                hesabınızda şüpheli bir aktivite fark ettiğinizde derhal bize bildirmelisiniz.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Hizmetler</h2>
              <h3 className="text-xl font-semibold text-white mb-3">4.1 Platform Hizmetleri</h3>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>Trading stratejisi oluşturma araçları</li>
                <li>Backtest ve simülasyon hizmetleri</li>
                <li>Canlı sinyal üretimi ve bildirimleri</li>
                <li>Strateji pazaryeri</li>
                <li>Partner/referans programı</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">4.2 Hizmet Değişiklikleri</h3>
              <p className="text-slate-300">
                Şirket, hizmetlerini önceden bildirimde bulunarak veya bulunmaksızın değiştirme, 
                askıya alma veya sonlandırma hakkını saklı tutar.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Ücretler ve Ödemeler</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Ücretsiz ve ücretli hizmetler platformda açıkça belirtilir</li>
                <li>Ücretli hizmetler için geçerli fiyatlar ödeme anında gösterilir</li>
                <li>Abonelikler aksi belirtilmedikçe otomatik olarak yenilenir</li>
                <li>İptal talepleri bir sonraki fatura döneminden itibaren geçerli olur</li>
                <li>Strateji satışlarında platform komisyonu %15'tir</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Strateji Geliştirici Kuralları</h2>
              <p className="text-slate-300 mb-4">
                Strateji yayınlayan kullanıcılar aşağıdaki kurallara uymakla yükümlüdür:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Yanıltıcı performans verileri paylaşmamak</li>
                <li>Gerçekçi olmayan getiri vaatlerinde bulunmamak</li>
                <li>Stratejinin risklerini açıkça belirtmek</li>
                <li>Üçüncü taraf haklarını ihlal etmemek</li>
                <li>Manipülatif veya hileli yöntemler kullanmamak</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Yasaklı Davranışlar</h2>
              <p className="text-slate-300 mb-4">
                Aşağıdaki davranışlar kesinlikle yasaktır:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Platform güvenliğini tehlikeye atacak faaliyetler</li>
                <li>Sahte hesap oluşturma veya kimlik taklidi</li>
                <li>Spam, zararlı yazılım veya kötü amaçlı içerik paylaşımı</li>
                <li>Diğer kullanıcıların haklarını ihlal etmek</li>
                <li>Yasalara aykırı faaliyetler</li>
                <li>Platformun normal işleyişini bozmak</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Fikri Mülkiyet</h2>
              <p className="text-slate-300 mb-4">
                Platform üzerindeki tüm içerik, tasarım, logo, yazılım ve diğer materyaller 
                Datex Bilişim AŞ'nin mülkiyetindedir. Kullanıcılar:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Oluşturdukları stratejilerin fikri mülkiyet haklarını korur</li>
                <li>Stratejilerini platformda yayınlayarak Şirket'e sınırlı lisans verir</li>
                <li>Başkalarının fikri mülkiyet haklarına saygı gösterir</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Sorumluluk Sınırlaması</h2>
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 mb-4">
                <p className="text-yellow-200 font-semibold mb-2">⚠️ Önemli Uyarı</p>
                <p className="text-slate-300">
                  Platform üzerinden sunulan stratejiler ve sinyaller <strong className="text-white">yatırım tavsiyesi değildir</strong>. 
                  Tüm yatırım kararları kullanıcının kendi sorumluluğundadır.
                </p>
              </div>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Şirket, stratejilerin performansını garanti etmez</li>
                <li>Geçmiş performans gelecek sonuçları garanti etmez</li>
                <li>Kullanıcıların yatırım kayıplarından Şirket sorumlu tutulamaz</li>
                <li>Teknik aksaklıklardan doğan zararlardan sorumluluk kabul edilmez</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">10. Hesap Askıya Alma ve Fesih</h2>
              <p className="text-slate-300 mb-4">
                Şirket, aşağıdaki durumlarda hesabınızı askıya alabilir veya feshedebilir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Kullanım koşullarının ihlali</li>
                <li>Yasadışı faaliyetler</li>
                <li>Diğer kullanıcılara zarar verici davranışlar</li>
                <li>Uzun süreli inaktivite</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">11. Uyuşmazlık Çözümü</h2>
              <p className="text-slate-300">
                Bu sözleşmeden doğan uyuşmazlıklarda Türkiye Cumhuriyeti kanunları uygulanır. 
                Uyuşmazlıkların çözümünde İzmir Mahkemeleri ve İcra Daireleri yetkilidir.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">12. İletişim</h2>
              <div className="p-4 rounded-lg bg-slate-800/50">
                <p className="text-slate-300 mb-2">
                  <strong className="text-white">Şirket:</strong> Datex Bilişim AŞ
                </p>
                <p className="text-slate-300 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <strong className="text-white">E-posta:</strong> info@borsamex.com
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Değişiklikler</h2>
              <p className="text-slate-300">
                Bu kullanım koşulları önceden bildirimde bulunarak değiştirilebilir. 
                Önemli değişiklikler e-posta yoluyla bildirilecektir. Değişikliklerden sonra 
                platformu kullanmaya devam etmeniz, güncellenmiş koşulları kabul ettiğiniz anlamına gelir.
              </p>
            </section>

          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/legal/privacy" className="text-slate-400 hover:text-primary transition-colors">
            Gizlilik Politikası
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/risk" className="text-slate-400 hover:text-primary transition-colors">
            Risk Uyarısı
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/cookies" className="text-slate-400 hover:text-primary transition-colors">
            Çerez Politikası
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
